import type { LucideIcon } from 'lucide-react'
import type { SupplyConfidence } from '@/api/client'
import { PROTECTION_ICON, REVIEWS_ICON, SUPPLIES_ICON } from '@/lib/nav-icons'
import { isAttentionEvent } from '@/lib/deposit-events'

/**
 * Point events - a deposit hold expiring, a delivery arriving - drawn as
 * labelled blocks in the day cell (CAP-913): an icon and the event in plain
 * words, in the same family as the stay and cleaning bars, so nobody has to
 * learn a legend or hover to read what happened.
 *
 * Everything about HOW an event looks is in `eventBlockMeta` below, and nothing
 * else decides it: the grid's blocks, the day list's rows and the legend all
 * ask it. A new layer adds a case there and a builder that turns its data into
 * `EventBlockSpec`s; the block, the overflow and the narrow-cell fallback come
 * with it (the review window, CAP-879, was the first to).
 */

/** Which layer an event belongs to: names its test ids and what it opens. */
export type EventBlockLayer = 'deposit' | 'supply' | 'review'

/**
 * What an event is, as far as its look goes. The words and style depend on
 * exactly these things and no others:
 *
 * - `layer` picks the icon (the sidebar's own: Protection for deposits,
 *   Supplies for supplies, Reviews for reviews);
 * - `planned` is "has not happened yet": outlined, and the words say so;
 *   recorded is filled;
 * - `type` / `kind` is what happened, in words;
 * - `confidence` is how sure the supply model is of a runout;
 * - `owed` is a review window that is open while the chase says your own
 *   review is still outstanding.
 */
export type EventBlockInput =
  | { layer: 'deposit'; type: string; planned: boolean }
  | {
      layer: 'supply'
      kind: 'runout' | 'delivery'
      /** The category's display name ("Batteries"). */
      category: string
      confidence: SupplyConfidence
    }
  | {
      layer: 'review'
      /** The window closing, the guest's review landing, or yours. */
      kind: 'closes' | 'guest' | 'host'
      planned: boolean
      owed: boolean
    }

export type EventBlockTone = 'routine' | 'attention' | 'uncertain'

export interface EventBlockMeta {
  Icon: LucideIcon
  /** The event in plain words: what the block says, and the start of its accessible name. */
  words: string
  /**
   * The same event in fewer words, for a cell too narrow for `words`: "Hold
   * expires" for "Deposit hold expires". The icon already says "deposit", so
   * the short form keeps the part that varies. Absent when `words` is short.
   */
  shortWords?: string
  tone: EventBlockTone
  /** Outlined (not happened yet, or only a forecast) rather than filled. */
  outlined: boolean
  /** Border, fill and text colours. Louder than a plain bar, never louder than a cleaned or needs-decision one. */
  classes: string
}

/**
 * Deposit event words, as {recorded, planned}. A planned event says "expected"
 * or is in the future tense ("expires", "ends"), because the outline alone is
 * colour-and-shape and the words must carry it too. A type this build has not
 * met falls back to the API's own name.
 */
const DEPOSIT_WORDS: Record<string, { recorded: string; planned: string }> = {
  hold_due: { recorded: 'Deposit hold due', planned: 'Deposit hold planned' },
  placed: { recorded: 'Deposit held', planned: 'Deposit hold planned' },
  renewed: { recorded: 'Deposit hold renewed', planned: 'Deposit renewal planned' },
  inspection_ends: { recorded: 'Inspection ended', planned: 'Inspection ends' },
  expected_lapse: { recorded: 'Deposit hold expired', planned: 'Deposit hold expires' },
  backstop_release: { recorded: 'Deposit released', planned: 'Deposit release expected' },
  released: { recorded: 'Deposit released', planned: 'Deposit release expected' },
  captured: { recorded: 'Deposit charged', planned: 'Deposit charge expected' },
  refunded: { recorded: 'Deposit refunded', planned: 'Deposit refund expected' },
  disputed: { recorded: 'Deposit disputed', planned: 'Deposit dispute expected' },
}

/** "Deposit hold expires" -> "Hold expires": the icon carries "deposit". */
function depositShortWords(words: string): string | undefined {
  const rest = words.startsWith('Deposit ') ? words.slice('Deposit '.length) : null
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : undefined
}

function depositWords(type: string, planned: boolean): string {
  const known = DEPOSIT_WORDS[type]
  if (known) return planned ? known.planned : known.recorded
  const name = type.replace(/_/g, ' ')
  return planned ? `Deposit: ${name} (planned)` : `Deposit: ${name}`
}

/** "Batteries" -> "batteries": the category reads as part of the sentence. */
function inSentence(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * Colours by tone and shape. Recorded events are a soft tint (never the solid
 * fill of a cleaned or needs-decision bar, which must stay the loudest thing on
 * the grid); planned and forecast events are an outline on the page's own
 * colour; an uncertain forecast is dashed and quiet, like a tentative cleaning.
 */
export const BLOCK_CLASSES: Record<EventBlockTone, { filled: string; outlined: string }> = {
  routine: {
    filled: 'border-mint bg-mint/20 text-text',
    outlined: 'border-mint bg-transparent text-text',
  },
  attention: {
    filled: 'border-amber bg-amber/20 text-text',
    outlined: 'border-amber bg-transparent text-text',
  },
  uncertain: {
    filled: 'border-dashed border-amber/70 bg-transparent text-textMuted',
    outlined: 'border-dashed border-amber/70 bg-transparent text-textMuted',
  },
}

/** The review layer's events: the window closing (outlined while open) and the reviews that landed (always recorded). */
function reviewMeta(input: Extract<EventBlockInput, { layer: 'review' }>): EventBlockMeta {
  const { kind, planned, owed } = input
  const tone: EventBlockTone = kind === 'closes' && owed ? 'attention' : 'routine'
  const outlined = kind === 'closes' && planned
  const words =
    kind === 'guest'
      ? 'Guest reviewed'
      : kind === 'host'
        ? 'You reviewed'
        : planned
          ? 'Review window closes'
          : 'Review window closed'
  return {
    Icon: REVIEWS_ICON,
    words,
    // The star already says "review".
    shortWords: kind === 'closes' ? words.replace('Review window', 'Window') : undefined,
    tone,
    outlined,
    classes: BLOCK_CLASSES[tone][outlined ? 'outlined' : 'filled'],
  }
}

/**
 * THE mapping: (layer, event type, planned or recorded, confidence) to icon,
 * words and style. Add a layer's events here.
 *
 * | Layer   | Event                          | Words                                  | Tone, shape                        |
 * | ------- | ------------------------------ | -------------------------------------- | ---------------------------------- |
 * | deposit | routine step, recorded         | "Deposit held", "Deposit hold renewed" | mint, filled                       |
 * | deposit | routine step, planned          | "Deposit hold expires", "Inspection ends" | mint, outlined                  |
 * | deposit | money moved or contested       | "Deposit refunded", "Deposit charged"  | amber, filled (outlined if planned) |
 * | supply  | delivery                       | "Delivered: batteries"                 | mint, filled                       |
 * | supply  | runout, medium or high confidence | "Out: batteries"                    | amber, outlined (it is a forecast) |
 * | supply  | runout, low or no confidence   | "Maybe out: batteries"                 | amber, dashed and muted            |
 * | review  | window open                    | "Review window closes"                 | mint, outlined (amber if you owe a review) |
 * | review  | window closed                  | "Review window closed"                 | mint, filled                       |
 * | review  | a review landed                | "Guest reviewed", "You reviewed"       | mint, filled                       |
 */
export function eventBlockMeta(input: EventBlockInput): EventBlockMeta {
  if (input.layer === 'deposit') {
    const tone: EventBlockTone = isAttentionEvent(input.type) ? 'attention' : 'routine'
    const outlined = input.planned
    const words = depositWords(input.type, input.planned)
    return {
      Icon: PROTECTION_ICON,
      words,
      shortWords: depositShortWords(words),
      tone,
      outlined,
      classes: BLOCK_CLASSES[tone][outlined ? 'outlined' : 'filled'],
    }
  }
  if (input.layer === 'review') return reviewMeta(input)
  const name = inSentence(input.category)
  if (input.kind === 'delivery') {
    return {
      Icon: SUPPLIES_ICON,
      words: `Delivered: ${name}`,
      tone: 'routine',
      outlined: false,
      classes: BLOCK_CLASSES.routine.filled,
    }
  }
  const sure = input.confidence === 'medium' || input.confidence === 'high'
  const tone: EventBlockTone = sure ? 'attention' : 'uncertain'
  return {
    Icon: SUPPLIES_ICON,
    words: `${sure ? 'Out' : 'Maybe out'}: ${name}`,
    tone,
    outlined: true,
    classes: BLOCK_CLASSES[tone].outlined,
  }
}

/** One event, ready to draw: a layer's builder makes these from its own data. */
export interface EventBlockSpec {
  /** Stable and unique across layers, for React. */
  key: string
  layer: EventBlockLayer
  meta: EventBlockMeta
  /** When, or how much: the tooltip's and the day list's second line ("Oct 15, 2:15 PM"). */
  detail: string | null
  /** What a click does, appended to the accessible name: "open deposit timeline". */
  action: string
  /** `data-*` attributes layers add for their own tests. */
  data?: Record<string, string>
  onOpen: () => void
}

/** "Deposit hold expires, Oct 15, 2:15 PM" */
export function eventBlockTitle(spec: EventBlockSpec): string {
  return spec.detail ? `${spec.meta.words}, ${spec.detail}` : spec.meta.words
}

/** A day shows this many rows of blocks; past it the last row is "+N more". */
export const MAX_BLOCK_ROWS_PER_DAY = 3
